import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../components/LanguageSwitcher';
import './Login.css';  

function Login({ setToken }: { setToken: (token: string) => void }) {
  const { t, i18n } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const navigate = useNavigate();
  const API_BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '') || '';

  const handleLogin = async () => {
    if (!email || !password) {
      setErrorMessage(t('auth.fillAllFields'));
      return;
    }

    setIsLoading(true);
    setErrorMessage('');

    try {
      const response = await fetch(`${API_BASE}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      const data = await response.json();
      if (data.access_token) {
        setToken(data.access_token);
        localStorage.setItem('token', data.access_token);
        navigate(`/${i18n.language}/chat`);
      } else if (response.status === 403 && data.detail) {
        setErrorMessage(data.detail);
      } else {
        setErrorMessage(t('auth.loginFailed'));
      }
    } catch (error) {
      console.error('Login error:', error);
      setErrorMessage(t('auth.loginFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleLogin();
    }
  };

  return (
    <div className="login-page">
      <div className="container py-4">
        <div className="login-card">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <Link to={`/${i18n.language}`} className="back-link">
              <i className="bi bi-arrow-left me-1"></i>
              {t('landing.backToHome')}
            </Link>
            <LanguageSwitcher variant="white-bg" />
          </div>

          {/* Logo/Icon */}
          <div className="login-icon mb-3">
            <i className="bi bi-person-circle"></i>
          </div>

          <h2 className="text-center mb-2">{t('auth.loginTitle')}</h2>
          <p className="text-center text-muted mb-4">
            {t('auth.loginSubtitle')}
          </p>

          {/* Error Message */}
          {errorMessage && (
            <div className="alert alert-danger alert-dismissible fade show" role="alert">
              <i className="bi bi-exclamation-triangle me-2"></i>{errorMessage}
              <button type="button" className="btn-close" onClick={() => setErrorMessage('')}></button>
            </div>
          )}

          {/* Form */}
          <div className="mb-3">
            <label className="form-label">{t('common.email')}</label>
            <div className="input-group">
              <span className="input-group-text">
                <i className="bi bi-envelope"></i>
              </span>
              <input
                type="email"
                className="form-control"
                placeholder="exemple@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyPress={handleKeyPress}
              />
            </div>
          </div>

          <div className="mb-4">
            <label className="form-label">{t('common.password')}</label>
            <div className="input-group">
              <span className="input-group-text">
                <i className="bi bi-lock"></i>
              </span>
              <input
                type={showPassword ? 'text' : 'password'}
                className="form-control"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyPress={handleKeyPress}
              />
              <button
                className="btn btn-outline-secondary toggle-password"
                type="button"
                onClick={() => setShowPassword(!showPassword)}
              >
                <i className={`bi ${showPassword ? 'bi-eye-slash' : 'bi-eye'}`}></i>
              </button>
            </div>
            <div className="text-end mt-1">
              <Link to={`/${i18n.language}/forgot-password`} className="text-muted small text-decoration-none">
                {t('auth.forgotPassword', 'Mot de passe oublié ?')}
              </Link>
            </div>
          </div>

          <button
            onClick={handleLogin}
            className="btn btn-primary w-100 login-btn"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2"></span>
                {t('common.loading')}
              </>
            ) : (
              <>
                <i className="bi bi-box-arrow-in-right me-2"></i>
                {t('auth.loginButton')}
              </>
            )}
          </button>

          <div className="divider my-4">
            <span>{t('common.or')}</span>
          </div>

          <p className="text-center mb-0">
            {t('auth.noAccount')}
            <Link to={`/${i18n.language}/register`} className="register-link">
              {t('auth.createAccount')}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}

export default Login;
